/**
 * 前台用户 API 客户端
 * 用于处理所有前台应用（/app）的 API 请求
 *
 * 命名规范：
 * - 前台使用 userApiClient
 * - 后台使用 adminApiClient（已存在）
 */

import { RetryStrategy, withTimeout } from "./retryStrategy";
import { RequestQueue, tokenRefreshQueue } from "./requestQueue";

// API 响应类型定义
export interface ApiSuccessResponse<T = any> {
  success: true;
  data: T;
  meta: Record<string, any>;
}

export interface ApiErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    details: Record<string, any>;
  };
}

export type ApiResponse<T = any> = ApiSuccessResponse<T> | ApiErrorResponse;

// API 异常类
export class ApiException extends Error {
  code: string;
  details: Record<string, any>;
  status: number;

  constructor(error: ApiErrorResponse["error"], status: number) {
    super(error.message);
    this.name = "ApiException";
    this.code = error.code;
    this.details = error.details;
    this.status = status;
  }

  // 判断是否为特定错误类型
  is(code: string): boolean {
    return this.code === code;
  }

  // 判断是否为验证错误
  isValidationError(): boolean {
    return this.code === "VALIDATION_ERROR";
  }

  // 判断是否为未授权错误
  isUnauthorized(): boolean {
    return this.code === "UNAUTHORIZED";
  }

  // 判断是否为禁止访问错误
  isForbidden(): boolean {
    return this.code === "FORBIDDEN";
  }

  // 判断是否为资源未找到错误
  isNotFound(): boolean {
    return this.code === "NOT_FOUND";
  }
}

// 请求配置
export interface RequestConfig extends RequestInit {
  params?: Record<string, any>;
  skipAuth?: boolean;
  /**
   * 是否启用重试（默认：true）
   */
  enableRetry?: boolean;
  /**
   * 请求超时时间（毫秒，默认：30000）
   */
  timeout?: number;
  /**
   * 是否启用去重（默认：true）
   */
  enableDeduplication?: boolean;
}

// API 客户端配置
export interface ApiClientConfig {
  baseURL?: string;
  /**
   * 是否启用重试（默认：true）
   */
  enableRetry?: boolean;
  /**
   * 最大重试次数（默认：3）
   */
  maxRetries?: number;
  /**
   * 基础延迟时间（毫秒，默认：1000）
   */
  baseDelay?: number;
  /**
   * 请求超时时间（毫秒，默认：30000）
   */
  timeout?: number;
  /**
   * 是否启用请求去重（默认：true）
   */
  enableDeduplication?: boolean;
  /**
   * 是否启用调试日志（默认：false）
   */
  debug?: boolean;
}

/**
 * 前台用户 API 客户端类
 *
 * 功能特性：
 * - 统一的请求封装
 * - 自动添加 CSRF Token
 * - 自动添加认证信息（Cookie-based session）
 * - 统一的错误处理
 * - 自动重试（指数退避）
 * - 请求队列管理
 * - 请求去重
 * - 超时控制
 * - 请求/响应拦截
 */
class UserApiClient {
  private baseURL: string;
  private retryStrategy: RetryStrategy;
  private requestQueue: RequestQueue;
  private config: Required<ApiClientConfig>;

  constructor(config: ApiClientConfig = {}) {
    this.config = {
      baseURL: config.baseURL || "/api/v1",
      enableRetry: config.enableRetry !== false,
      maxRetries: config.maxRetries || 3,
      baseDelay: config.baseDelay || 1000,
      timeout: config.timeout || 30000,
      enableDeduplication: config.enableDeduplication !== false,
      debug: config.debug || false,
    };

    this.baseURL = this.config.baseURL;
    this.retryStrategy = new RetryStrategy({
      maxRetries: this.config.maxRetries,
      baseDelay: this.config.baseDelay,
    });
    this.requestQueue = new RequestQueue({
      enableDeduplication: this.config.enableDeduplication,
      debug: this.config.debug,
    });
  }

  /**
   * 获取 CSRF Token
   */
  private getCSRFToken(): string {
    const meta = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]'
    );
    return meta?.content || "";
  }

  /**
   * 构建完整的 URL（包含查询参数）
   */
  private buildURL(endpoint: string, params?: Record<string, any>): string {
    const url = new URL(endpoint, window.location.origin);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return url.toString();
  }

  /**
   * 生成请求唯一标识（用于去重）
   */
  private generateRequestKey(url: string, config: RequestConfig): string {
    const method = config.method || "GET";
    const body = config.body ? String(config.body) : "";
    return `${method}:${url}:${body}`;
  }

  /**
   * 调试日志
   */
  private log(...args: any[]): void {
    if (this.config.debug) {
      console.log("[UserApiClient]", ...args);
    }
  }

  /**
   * 通用请求方法（核心）
   */
  public async request<T = any>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<T> {
    const {
      params,
      skipAuth,
      enableRetry = this.config.enableRetry,
      timeout = this.config.timeout,
      enableDeduplication = this.config.enableDeduplication,
      ...fetchConfig
    } = config;

    // 构建 URL
    const url = this.buildURL(`${this.baseURL}${endpoint}`, params);

    // 生成请求 key（用于去重）
    const requestKey = this.generateRequestKey(url, fetchConfig);

    this.log("Request:", requestKey);

    // 执行请求函数
    const executeRequest = async (): Promise<T> => {
      // 构建请求头
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.getCSRFToken(),
        ...(fetchConfig.headers as Record<string, string>),
      };

      // 如果 body 是 FormData，删除 Content-Type，让浏览器自动设置
      if (fetchConfig.body instanceof FormData) {
        delete headers["Content-Type"];
      }

      // 添加 Token 认证（如果有，用于移动端）
      if (!skipAuth) {
        const token = localStorage.getItem("user_access_token");
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
      }

      // 发起请求（带超时控制）
      const fetchPromise = fetch(url, {
        ...fetchConfig,
        credentials: "same-origin",
        headers,
      });

      // 应用超时控制
      const response = await withTimeout(
        fetchPromise,
        timeout,
        new Error(`Request timeout after ${timeout}ms`)
      );

      // 解析响应
      const json: ApiResponse<T> = await response.json();

      // 检查响应状态
      if (!response.ok || !json.success) {
        const error = new ApiException(
          (json as ApiErrorResponse).error,
          response.status
        );
        // 将 status 附加到 error 对象（用于重试判断）
        (error as any).status = response.status;
        throw error;
      }

      // 返回数据
      return (json as ApiSuccessResponse<T>).data;
    };

    // 包装：重试策略
    const withRetry = async (): Promise<T> => {
      if (enableRetry) {
        return this.retryStrategy.execute(
          executeRequest,
          (error, attemptNumber, delay) => {
            this.log(
              `Request failed, retrying in ${delay}ms (attempt ${attemptNumber + 1}/${this.config.maxRetries})`,
              { error, url }
            );
          }
        );
      } else {
        return executeRequest();
      }
    };

    // 包装：请求队列（可选去重）
    if (enableDeduplication) {
      return this.requestQueue.enqueue(requestKey, withRetry);
    } else {
      return withRetry();
    }
  }

  /**
   * GET 请求
   */
  async get<T = any>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: "GET",
    });
  }

  /**
   * POST 请求
   */
  async post<T = any>(
    endpoint: string,
    data?: any,
    config?: RequestConfig
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: "POST",
      body: data instanceof FormData ? data : JSON.stringify(data),
    });
  }

  /**
   * PATCH 请求
   */
  async patch<T = any>(
    endpoint: string,
    data?: any,
    config?: RequestConfig
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: "PATCH",
      body: data instanceof FormData ? data : JSON.stringify(data),
    });
  }

  /**
   * PUT 请求
   */
  async put<T = any>(
    endpoint: string,
    data?: any,
    config?: RequestConfig
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: "PUT",
      body: data instanceof FormData ? data : JSON.stringify(data),
    });
  }

  /**
   * DELETE 请求
   */
  async delete<T = any>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: "DELETE",
    });
  }

  /**
   * 上传文件
   */
  async upload<T = any>(
    endpoint: string,
    formData: FormData,
    config?: RequestConfig
  ): Promise<T> {
    const url = this.buildURL(`${this.baseURL}${endpoint}`, config?.params);

    const headers: HeadersInit = {
      Accept: "application/json",
      "X-CSRF-Token": this.getCSRFToken(),
      // 不设置 Content-Type，让浏览器自动设置（multipart/form-data with boundary）
    };

    const response = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers,
      body: formData,
      ...config,
    });

    const json: ApiResponse<T> = await response.json();

    if (!response.ok || !json.success) {
      throw new ApiException((json as ApiErrorResponse).error, response.status);
    }

    return (json as ApiSuccessResponse<T>).data;
  }
}

// 导出单例
export const userApiClient = new UserApiClient();

// 导出为默认对象
export default userApiClient;
